import { useEffect, useRef, useState } from "react";
import { PostTypeFilter, SourceTypeFilter } from "../../api/Reports";

// The sidebar turns on split counts as soon as a filter is checked. Count
// requests wait for the debounce, so a generation started with the current
// filters is the only response allowed to replace the plain total.
export function useFilteredCountGeneration(filterKey: string) {
  const generation = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    generation.current += 1;
    setReady(false);
  }, [filterKey]);

  const capture = () => generation.current;

  const settle = (capturedGeneration: number) => {
    if (capturedGeneration === generation.current) {
      setReady(true);
    }
  };

  return { ready, capture, settle };
}

export function trackCountResponses(
  generation: number,
  settle: (generation: number) => void
) {
  let pending = 3;
  let failed = false;
  const finish = (ok: boolean) => {
    if (!ok) failed = true;
    pending -= 1;
    if (pending === 0 && !failed) settle(generation);
  };
  return {
    onSuccess: () => finish(true),
    onError: () => finish(false),
  };
}

type CountFilterInput = {
  behaviorFilter: readonly string[];
  keywordFilter: readonly string[];
  topicFilter: readonly string[];
  itemTypeFilter: readonly string[];
  sourceTypeFilter: Partial<SourceTypeFilter>;
  postTypeFilter: Partial<PostTypeFilter>;
  dateFilter: { fromDate: string; toDate: string };
};

type ReviewCountRequest = {
  behaviors?: readonly string[];
  keywords?: readonly string[];
  topics?: readonly string[];
  item_type_name?: readonly string[];
  item_source_name?: readonly string[];
  item_post_type?: readonly string[];
  min_date?: string;
  max_date?: string;
};

function selectedKeys(
  flags: Partial<SourceTypeFilter> | Partial<PostTypeFilter>
): string[] {
  return Object.entries(flags)
    .filter(([, selected]) => selected)
    .map(([key]) => key);
}

function sameList(
  actual: readonly string[] | undefined,
  expected: readonly string[]
): boolean {
  if (!actual || actual.length !== expected.length) return false;
  return actual.every((value, index) => value === expected[index]);
}

// The first count request can still be the empty useState value. That request
// matches only when no filters are applied. A later request is usable once it
// carries the same lists the sidebar is filtering on.
export function reviewRequestMatchesFilters(
  request: ReviewCountRequest,
  filters: CountFilterInput
): boolean {
  const sources = selectedKeys(filters.sourceTypeFilter);
  const postTypes = selectedKeys(filters.postTypeFilter);
  const filtersApplied =
    filters.behaviorFilter.length > 0 ||
    filters.keywordFilter.length > 0 ||
    filters.topicFilter.length > 0 ||
    filters.itemTypeFilter.length > 0 ||
    sources.length > 0 ||
    postTypes.length > 0 ||
    filters.dateFilter.fromDate !== "" ||
    filters.dateFilter.toDate !== "";
  const requestBuilt =
    request.behaviors !== undefined &&
    request.keywords !== undefined &&
    request.topics !== undefined;
  if (!requestBuilt) return !filtersApplied;
  return (
    sameList(request.behaviors, filters.behaviorFilter) &&
    sameList(request.keywords, filters.keywordFilter) &&
    sameList(request.topics, filters.topicFilter) &&
    sameList(request.item_type_name, filters.itemTypeFilter) &&
    sameList(request.item_source_name ?? [], sources) &&
    sameList(request.item_post_type ?? [], postTypes) &&
    (request.min_date ?? "") === filters.dateFilter.fromDate &&
    (request.max_date ?? "") === filters.dateFilter.toDate
  );
}

export function countFilterKey(filters: {
  reportUuid: string;
  sourceTypeFilter: unknown;
  postTypeFilter: unknown;
  dateFilter: unknown;
  behaviorFilter: readonly string[];
  keywordFilter: readonly string[];
  topicFilter: readonly string[];
  itemTypeFilter: readonly string[];
}): string {
  return JSON.stringify(filters);
}
